import type { StaticListings } from '@/types/StaticListings';
import React, { useEffect, useRef, useState } from 'react'
import type { ListingTypeDetailsRef } from './application/ListingTypeDetails';
import type { PropertyDetailsRef } from './application/PropertyDetails';
import type { UnitDetailsRef } from './application/UnitDetails';
import type { PriceDetailsRef } from './application/PriceDetails';
import type { DescriptionDetailsRef } from './application/DescriptionDetails';
import type { GalleryDetailsRef } from './application/GalleryDetails';
import api from '@/lib/axiosInstance';
import UnitDetails from './application/UnitDetails';
import PropertyDetails from './application/PropertyDetails';
import ListingTypeDetails from './application/ListingTypeDetails';
import { Button } from '@/components/ui/button';
import GalleryDetails from './application/GalleryDetails';
import DescriptionDetails from './application/DescriptionDetails';
import AIListingAssistant from '@/components/listings/AIListingAssistant';
import PriceDetails from './application/PriceDetails';
import { toast } from 'sonner';
import type { PropertyTypes } from '@/types/PropertyTypes';
import type { PropertySubTypes } from '@/types/PropertySubTypes';
import type { FacilityBenefits } from '@/types/FacilityBenefits';
import { useNavigate, useParams } from 'react-router-dom';
import type { UnitFeatures } from '@/types/UnitFeatures';
import type { PropertyListings } from '@/types/PropertyListings';
import PlatformPostingDetails, { type PlatformPostingDetailsRef } from './application/PlatformPostingDetails';
import { Spinner } from '@/components/ui/shadcn-io/spinner';
import Alert from '@/components/Alert';
import { motion } from 'framer-motion';
import { Alert as _Alert, AlertDescription } from "@/components/ui/alert"
import { useSelector } from 'react-redux';
import type { RootState } from '@/lib/redux/store';
import { Skeleton } from "@/components/ui/skeleton";
import type { States } from '@/types/States';
import type { PropertyListingSharedAgents } from '@/types/PropertyListingSharedAgents';
import LocationMappingDetails, { type LocationMappingDetailsRef } from './application/LocationMappingDetails'
import type { Districts } from '@/types/Districts';
import type { Subdistricts } from '@/types/Subdistricts';
import type { LandTitleTypes } from '@/types/LandTitleTypes';

interface OwnerForm {
    firstName: string;
    lastName: string;
    email: string;
    contact: string;
    dob: Date | undefined;
    requestSentAt?: string | null;
    status?: number | null;
    reason?: string | null;
    avatar?: string | null;
}

const ListingEdit = () => {
    const agent = useSelector((state: RootState) => state.auth.agent);
    const { listingId } = useParams();
    const [index, setIndex] = useState<number>(8);
    const navigate = useNavigate();
    const [listing, setListing] = useState<PropertyListings | null>(null);
    const [preloaded, setPreloaded] = useState<boolean>(false);
    const [landTitleTypes, setLandTitleTypes] = useState<LandTitleTypes[]>([]);
    const [districts, setDistricts] = useState<Districts[]>([]);
    const [subdistricts, setSubdistricts] = useState<Subdistricts[]>([]);
    const [selectedDistrict, setSelectedDistrict] = useState<string>('');
    const [selectedSubdistrict, setSelectedSubdistrict] = useState<string>('');
    const [selectedTitleTypeCode, setSelectedTitleTypeCode] = useState<string>('');
    const [jupemNo, setJupemNo] = useState<string>('');
    const [ownershipNumber, setOwnershipNumber] = useState<string>('');
    const [sizeOfLand, setSizeOfLand] = useState<string>('');
    const [lotNo, setLotNo] = useState<string>('');
    const [markAsDuplicate, setMarkAsDuplicate] = useState<boolean>(false);
    const [referenceListing, setReferenceListing] = useState<PropertyListings | null>(null);
    const [excludeMapPin, setExcludeMapPin] = useState<boolean>(false);

    // Index 1 
    const [leaseTerms, setLeaseTerms] = useState<StaticListings[]>([]);
    const [listingTypes, setListingTypes] = useState<StaticListings[]>([]);
    const [projectTypes, setProjectTypes] = useState<StaticListings[]>([]);
    const [rentalTypes, setRentalTypes] = useState<StaticListings[]>([]);
    const [selectedProjectType, setSelectedProjectType] = useState<number | null>(null);
    const [forSaleOrRentOptions, setForSaleOrRentOptions] = useState<StaticListings[]>([]);
    const [selectedListingType, setSelectedListingType] = useState<number | null>(null);
    const [selectedLeaseTerm, setSelectedLeaseTerm] = useState<number | null>(null);
    const [selectedForSaleOrRent, setSelectedForSaleOrRent] = useState<number | null>(null);
    const [selectedRentalType, setSelectedRentalType] = useState<number | null>(null);
    const [selectedAvailableDate, setSelectedAvailableDate] = useState<Date | undefined>(undefined);
    const [listingReferenceNumber, setListingReferenceNumber] = useState<string>('');
    const [coAgencyListing, setCoAgencyListing] = useState<boolean>(false);
    const [coShareListing, setCoShareListing] = useState<boolean>(false);
    const [forAuction, setForAuction] = useState<boolean>(false);
    const [forTender, setForTender] = useState<boolean>(false);
    const [selectedCoAgencyAgents, setSelectedCoAgencyAgents] = useState<{
        id: number,
        firstName: string,
        lastName: string
    }[]>([]);
    const [selectedCoShareAgents, setSelectedCoShareAgents] = useState<{
        id: number,
        firstName: string,
        lastName: string,
        status: number;
        coShareId: number;
    }[]>([]);

    // Index 2
    const [states, setStates] = useState<States[]>([]);
    const [manualAddress, setManualAddress] = useState<boolean>(false);
    const [rawAddress, setRawAddress] = useState<string>('');
    const [selectedState, setSelectedState] = useState<number | null>(null);
    const [selectedCity, setSelectedCity] = useState<number | null>(null);
    const [selectedTownship, setSelectedTownship] = useState<string>('');
    const [selectedStreet, setSelectedStreet] = useState<string>('');
    const [selectedPostalCode, setSelectedPostalCode] = useState<string>('');
    const [selectedUnitNumber, setSelectedUnitNumber] = useState<string>('');
    const [noTownship, setNoTownship] = useState<boolean>(false);
    const [coord, setCoord] = useState<{
        lat: number | null,
        lng: number | null
    }>({
        lat: null,
        lng: null
    });
    const [useTrueLocation, setUseTrueLocation] = useState<boolean>(false);
    const [radius, setRadius] = useState<number| null>(null);
    const [electricityBill, setElectricityBill] = useState<File | string | null>(null);
    const [waterBill, setWaterBill] = useState<File | string | null>(null);

    // Index 3
    const [propertyTypes, setPropertyTypes] = useState<PropertyTypes[]>([]);
    const [propertyUnitTypes, setPropertyUnitTypes] = useState<StaticListings[]>([]);
    const [propertySubTypes, setPropertySubTypes] = useState<PropertySubTypes[] | null>(null);
    const [titleTypes, setTitleTypes] = useState<StaticListings[]>([]);
    const [zoning, setZoning] = useState<StaticListings[]>([]);
    const [directions, setDirections] = useState<StaticListings[]>([]);
    const [tenures, setTenures] = useState<StaticListings[]>([]);
    const [facilityBenefits, setFacilityBenefits] = useState<FacilityBenefits[] | null>(null);
    const [yearOfCompletion, setYearOfCompletion] = useState<string>('');
    const [selectedDirection, setSelectedDirection] = useState<number | null>(null);
    const [selectedZoning, setSelectedZoning] = useState<number | null>(null);
    const [selectedTenure, setSelectedTenure] = useState<number | null>(null);
    const [selectedTitleType, setSelectedTitleType] = useState<number | null>(null);
    const [selectedPropertyType, setSelectedPropertyType] = useState<number | null>(null);
    const [selectedPropertySubType, setSelectedPropertySubType] = useState<number | null>(null);
    const [selectedPropertyUnitType, setSelectedPropertyUnitType] = useState<number | null>(null);
    const [selectedFacilityBenefits, setSelectedFacilityBenefits] = useState<number[]>([]);
    const [selectedLandTypeTitle, setSelectedLandTypeTitle] = useState<number | null>(null);
    const [isBumiLot, setIsBumiLot] = useState<number | null>(null);
    const [landTypeTitles, setLandTypeTitles] = useState<StaticListings[]>([]);

    // Index 4
    const [electricityPhases, setElectricityPhases] = useState<StaticListings[]>([]);
    const [unitFeatures, setUnitFeatures] = useState<UnitFeatures[] | null>(null);
    const [furnishingConditions, setFurnishingConditions] = useState<StaticListings[]>([]);
    const [noOfBathrooms, setNoOfBathrooms] = useState<number | null>(null);
    const [noOfBedrooms, setNoOfBedrooms] = useState<number | null>(null);
    const [noOfMaidRooms, setNoOfMaidRooms] = useState<number | null>(null);
    const [noOfParkingSpots, setNoOfParkingSpots] = useState<number | null>(null);
    const [builtUp, setBuiltUp] = useState<string>('');
    const [selectedBuiltUpUnit, setSelectedBuiltUpUnit] = useState<string>('ft');
    const [floorLoading, setFloorLoading] = useState<string>('');
    const [selectedFloorLoadingUnit, setSelectedFloorLoadingUnit] = useState<string>('tsm');
    const [ceilingHeight, setCeilingHeight] = useState<string>('');
    const [selectedCeilingHeightUnit, setSelectedCeilingHeightUnit] = useState<string>('ft');
    const [floorDetails, setFloorDetails] = useState<{
        block: string;
        floor: string;
        unit: string;
    }>({
        block: '',
        floor: '',
        unit: ''
    });
    const [builtUpDimensions, setBuiltUpDimensions] = useState<{
        width: string;
        length: string;
    }>({
        width: '',
        length: ''
    });
    const [selectedBuiltUpDimensionUnit, setSelectedBuiltUpDimensionUnit] = useState<string>('ft');
    const [landArea, setLandArea] = useState<string>('');
    const [selectedLandAreaUnit, setSelectedLandAreaUnit] = useState<string>('ft');
    const [landAreaDimensions, setLandAreaDimensions] = useState<{
        width: string,
        length: string
    }>({
        width: '',
        length: ''
    });
    const [selectedLandAreaDimensionUnit, setSelectedLandAreaDimensionUnit] = useState<string>('ft');
    const [selectedUnitFeatures, setSelectedUnitFeatures] = useState<number[]>([]);
    const [selectedFurnishingCondition, setSelectedFurnishingCondition] = useState<number | null>(null);
    const [selectedElectricityPhase, setSelectedElectricityPhase] = useState<number | null>(null);
    const [electricitySupply, setElectricitySupply] = useState<number | null>(null);
    const [selectedElectricitySupplyUnit, setSelectedElectricitySupplyUnit] = useState<string>('amp');
    const [totalCargoLifts, setTotalCargoLifts] = useState<number | null>(null);
    const [totalPassengerLifts, setTotalPassengerLifts] = useState<number | null>(null);
    const [maximumLiftCapacity, setMaximumLiftCapacity] = useState<number | null>(null);

    const [builtUpUnits, setBuiltUpUnits] = useState<StaticListings[]>([]);
    const [builtUpDimensionUnits, setBuiltUpDimensionUnits] = useState<StaticListings[]>([]);
    const [landAreaUnits, setLandAreaUnits] = useState<StaticListings[]>([]);
    const [landAreaDimensionUnits, setLandAreaDimensionUnits] = useState<StaticListings[]>([]);
    const [floorLoadingUnits, setFloorLoadingUnits] = useState<StaticListings[]>([]);
    const [electricitySupplyUnits, setElectricitySupplyUnits] = useState<StaticListings[]>([]);
    const [ceilingHeightUnits, setCeilingHeightUnits] = useState<StaticListings[]>([]);

    const [pricingTypes, setPricingTypes] = useState<StaticListings[]>([]);
    const [selectedPricingType, setSelectedPricingType] = useState<number | null>(null);
    const [price, setPrice] = useState<number | null>(null);
    const [maintenanceFee, setMaintenanceFee] = useState<number | null>(null);

    const [description, setDescription] = useState<string>('');
    const [headline, setHeadline] = useState<string>('');
    const [hideBlockNoWhenPublished, setHideBlockNoWhenPublished] = useState<boolean>(false);

    const locationMappingDetailsRef = useRef<LocationMappingDetailsRef>(null);
    const listingTypeDetailsRef = useRef<ListingTypeDetailsRef>(null);
    const propertyDetailsRef = useRef<PropertyDetailsRef>(null);
    const unitDetailsRef = useRef<UnitDetailsRef>(null);
    const priceDetailsRef = useRef<PriceDetailsRef>(null);
    const descriptionDetailsRef = useRef<DescriptionDetailsRef>(null);

    /** Fills the form from an AI draft. The listing still goes through the normal save. */
    const applyAICopy = ({ headline: newHeadline, description: newDescription }: { headline?: string; description?: string }) => {
        if (newHeadline !== undefined) setHeadline(newHeadline);
        if (newDescription !== undefined) setDescription(newDescription);
    };
    const galleryDetailsRef = useRef<GalleryDetailsRef>(null);
    const platformPostingDetailsRef = useRef<PlatformPostingDetailsRef>(null);
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [owners, setOwners] = useState<OwnerForm[]>([]);
    const [ownerValidationErrors, setOwnerValidationErrors] = useState<{[key: number]: string[]}>({});
    const [requireOwnerValidation, setRequireOwnerValidation] = useState<boolean>(false);
    const [isInternal, setIsInternal] = useState<boolean>(false);

    const steps = [
        'Listing Type', 
        'Property Details',
        'Location Mapping',
        'Unit Details', 
        'Price',
        'Description', 
        'Gallery', 
        'Platform posting', 
    ];

    const getListing = async () => {
        try {
            const response = await api.get(`/listings/${listingId}`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            const listing = response.data.listing;
            if(listing.createdBy !== agent.id) {
                if(!listing.sharedAgents.find((x : PropertyListingSharedAgents) => x.agentId === agent.id)) {
                    toast.error('Unauthorized access.');
                    navigate('/listings');
                }
            }
            setListing(listing);
        } catch (err: any) {
            const error = err.response ? err.response.data.error : err.message;
            toast.error(error);
        }
    }

    const getListingCriterias = async () => {
        try {
            const response = await api.get(`/listings/criterias`);
            if (!response.data.status) {
                throw new Error(response.data.error);
            }
            setLandTitleTypes(response.data.landTitleTypes);
            setDistricts(response.data.districts);
            setSubdistricts(response.data.subdistricts);
            setZoning(response.data.zoning);
            setProjectTypes(response.data.projectTypes);
            setLeaseTerms(response.data.leaseTerms);
            setStates(response.data.states);
            setPropertyTypes(response.data.propertyTypes);
            setPropertyUnitTypes(response.data.propertyUnitTypes);
            setTitleTypes(response.data.titleTypes);
            setListingTypes(response.data.listingTypes);
            setDirections(response.data.directions);
            setTenures(response.data.tenures);
            setFacilityBenefits(response.data.facilityBenefits);
            setForSaleOrRentOptions(response.data.forSaleOrRentOptions);
            setRentalTypes(response.data.rentalTypes);
            setFurnishingConditions(response.data.furnishingConditions);
            setUnitFeatures(response.data.unitFeatures);
            setElectricityPhases(response.data.electricityPhases);
            setPricingTypes(response.data.pricingTypes);
            setLandTypeTitles(response.data.landTypeTitles);
            setBuiltUpUnits(response.data.builtUpUnits);
            setLandAreaUnits(response.data.landAreaUnits);
            setBuiltUpDimensionUnits(response.data.builtUpDimensionUnits);
            setLandAreaDimensionUnits(response.data.landAreaDimensionUnits);
            setFloorLoadingUnits(response.data.floorLoadingUnits);
            setElectricitySupplyUnits(response.data.electricitySupplyUnits);
            setCeilingHeightUnits(response.data.ceilingHeightUnits);
        } catch (err: any) {
            const error = err.response ? err.response.data.error : err.message;
            toast.error(error);
        } 
    }

    const getPropertySubTypes = async () => {
        try {
            const response = await api.get(`/listings/properties/types/${selectedPropertyType}`);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            setPropertySubTypes(response.data.propertySubTypes);
        } catch (err: any) {
            const error = err.response ? err.response.data.error : err.message;
            toast.error(error);
        }
    }

    const handleSubmission = async (e: any) => {
        e.preventDefault();
        try {   
            if(index === 1 && listingTypeDetailsRef.current) {
                const ret = listingTypeDetailsRef.current.callback();
                if(ret) {
                    setIndex(2);
                }
            } else if(index === 2 && propertyDetailsRef.current){
                const ret = propertyDetailsRef.current.callback();
                if(ret) {
                    setIndex(3);
                }
            } else if(index === 3 && locationMappingDetailsRef.current) {
                const ret = await locationMappingDetailsRef.current.callback();
                if(ret) {
                    setIndex(4);
                }
            } else if(index === 4 && unitDetailsRef.current) {
                const ret = unitDetailsRef.current.callback();
                if(ret) {
                    setIndex(5);
                }
            } else if(index === 5 && priceDetailsRef.current) {
                const ret = priceDetailsRef.current.callback();
                if(ret) {
                    setIndex(6);
                }
            } else if(index === 6 && descriptionDetailsRef.current) {
                const ret = descriptionDetailsRef.current.callback();
                if(ret) {
                    setIndex(7);
                }
            } else if(index === 7 && galleryDetailsRef.current) {
                const ret = galleryDetailsRef.current.callback();
                if(ret) {
                    setIndex(8);
                }
            } else if(index === 8 && platformPostingDetailsRef.current) {
                if (!validateOwners()) {
                    throw new Error('Please complete all required fields for all owners.');
                }
                setSubmitting(true);
                var formdata = new FormData();
                formdata.append('listingTypeId', String(selectedListingType ?? ''));
                formdata.append('projectTypeId', String(selectedProjectType ?? ''));
                formdata.append('leaseTermId', String(selectedLeaseTerm ?? ''));
                formdata.append('forSaleOrRentId', String(selectedForSaleOrRent ?? ''));
                formdata.append('rentalTypeId', String(selectedRentalType ?? ''));
                formdata.append('availableDate', selectedAvailableDate ? selectedAvailableDate.toISOString() : '');
                formdata.append('listingReferenceNumber', listingReferenceNumber ?? '');
                formdata.append('coAgencyListing', String(coAgencyListing ? 1 : 0));
                formdata.append('coShareListing', String(coShareListing ? 1 : 0));
                formdata.append('forAuction', String(forAuction ? 1 : 0));
                formdata.append('forTender', String(forTender ? 1 : 0));
                formdata.append('coShareAgents', JSON.stringify(selectedCoShareAgents));
                formdata.append('coAgencyAgents', JSON.stringify(selectedCoAgencyAgents));
                if(waterBill instanceof File) {
                    formdata.append('waterBill', waterBill);
                }
                if(electricityBill instanceof File) {
                    formdata.append('electricityBill', electricityBill);
                }
                formdata.append('useTrueLocation', String(useTrueLocation ? 1 : 0));
                formdata.append('lat', String(coord?.lat ?? ''));
                formdata.append('lng', String(coord?.lng ?? ''));
                formdata.append('radius', String(radius));
                formdata.append('manualAddress', String(manualAddress ? 1: 0));
                formdata.append('rawAddress', rawAddress ?? '');
                formdata.append('stateId', String(selectedState ?? ''));
                formdata.append('cityId', String(selectedCity ?? ''));
                formdata.append('township', selectedTownship ?? '');
                formdata.append('street', selectedStreet ?? '');
                formdata.append('unit', selectedUnitNumber ?? '');
                formdata.append('postcode', String(selectedPostalCode ?? ''));
                formdata.append('unitNumber', selectedUnitNumber ?? '');
                formdata.append('noTownship', String(noTownship ? 1 : 0));
        
                formdata.append('propertyTypeId', String(selectedPropertyType ?? ''));
                formdata.append('propertySubTypeId', String(selectedPropertySubType ?? ''));
                formdata.append('propertyUnitTypeId', String(selectedPropertyUnitType ?? ''));
                formdata.append('zoningId', String(selectedZoning ?? ''));
                formdata.append('tenureId', String(selectedTenure ?? ''));
                formdata.append('titleTypeId', String(selectedTitleType ?? ''));
                formdata.append('directionId', String(selectedDirection ?? ''));
                formdata.append('yearOfCompletion', yearOfCompletion ?? '');
                formdata.append('landTypeTitleId', String(selectedLandTypeTitle ?? ''));
                formdata.append('isBumiLot', String(isBumiLot));
                
                selectedFacilityBenefits.forEach((id) => {
                    formdata.append(`facilityBenefits`, String(id));
                });
        
                formdata.append('noOfBathrooms', String(noOfBathrooms ?? ''));
                formdata.append('noOfBedrooms', String(noOfBedrooms ?? ''));
                formdata.append('noOfMaidRooms', String(noOfMaidRooms ?? ''));
                formdata.append('noOfParkingSpots', String(noOfParkingSpots ?? ''));
                formdata.append('hideBlockNoWhenPublished', String(hideBlockNoWhenPublished ? 1 : 0));
                formdata.append('unitNumberBlock', floorDetails.block ?? '');
                formdata.append('unitNumberFloor', floorDetails.floor ?? '');
                formdata.append('unitNumberUnit', floorDetails.unit ?? '');
                
                formdata.append('builtUp', builtUp ?? '');
                formdata.append('builtUpUnit', selectedBuiltUpUnit ?? '');
                formdata.append('builtUpWidth', builtUpDimensions.width ?? '');
                formdata.append('builtUpLength', builtUpDimensions.length ?? '');
                formdata.append('builtUpDimensionUnit', selectedBuiltUpDimensionUnit ?? '');
                formdata.append('floorLoading', floorLoading ?? '');
                formdata.append('floorLoadingUnit', selectedFloorLoadingUnit ?? '');
                formdata.append('ceilingHeight', ceilingHeight ?? '');
                formdata.append('ceilingHeightUnit', selectedCeilingHeightUnit ?? '');
                formdata.append('landArea', landArea ?? '');
                formdata.append('landAreaUnit', selectedLandAreaUnit ?? '');
                formdata.append('landAreaWidth', landAreaDimensions.width ?? '');
                formdata.append('landAreaLength', landAreaDimensions.length ?? '');
                formdata.append('landAreaDimensionUnit', selectedLandAreaDimensionUnit ?? '');

                formdata.append('furnishingConditionId', String(selectedFurnishingCondition ?? ''));
                formdata.append('electricityPhaseId', String(selectedElectricityPhase ?? ''));
                formdata.append('electricitySupply', String(electricitySupply ?? ''));
                formdata.append('electricitySupplyUnit', String(selectedElectricitySupplyUnit ?? ''));
                formdata.append('totalCargoLifts', String(totalCargoLifts ?? ''));
                formdata.append('totalPassengerLifts', String(totalPassengerLifts ?? ''));
                formdata.append('maximumLiftCapacity', String(maximumLiftCapacity ?? ''));

                selectedUnitFeatures.forEach((id) => {
                    formdata.append('unitFeatures', String(id));
                });
        
                formdata.append('selectedPricingType', String(selectedPricingType ?? ''));
                formdata.append('price', String(price ?? ''));
                formdata.append('maintenanceFee', String(maintenanceFee ?? ''));
        
                formdata.append('headline', headline ?? '');
                formdata.append('description', description ?? '');

                formdata.append('owners', JSON.stringify(owners));
                formdata.append('requireOwnerValidation', String(requireOwnerValidation ? 1 : 0));
                formdata.append('isInternal', String(isInternal ? 1 : 0));

                const response = await api.post(`/listings/${listingId}`, formdata);
                if(!response.data.status) {
                    throw new Error(response.data.error);
                }
                navigate('/listings');
            }
        } catch (err: any) {
            const error = err.response ? err.response.data.error : err.message;
            toast.error(error);
        } finally {
            setSubmitting(false);
        }
    }

    const publishListing = async () => {
        try {
            var formdata = new FormData();
            formdata.append('listingTypeId', String(selectedListingType ?? ''));
            formdata.append('projectTypeId', String(selectedProjectType ?? ''));
            formdata.append('leaseTermId', String(selectedLeaseTerm ?? ''));
            formdata.append('forSaleOrRentId', String(selectedForSaleOrRent ?? ''));
            formdata.append('rentalTypeId', String(selectedRentalType ?? ''));
            formdata.append('availableDate', selectedAvailableDate ? selectedAvailableDate.toISOString() : '');
            formdata.append('listingReferenceNumber', listingReferenceNumber ?? '');
            formdata.append('coAgencyListing', String(coAgencyListing ? 1 : 0));
            formdata.append('coShareListing', String(coShareListing ? 1 : 0));
            formdata.append('forAuction', String(forAuction ? 1 : 0));
            formdata.append('forTender', String(forTender ? 1 : 0));
            formdata.append('coShareAgents', JSON.stringify(selectedCoShareAgents));
            formdata.append('coAgencyAgents', JSON.stringify(selectedCoAgencyAgents));
            
    
            formdata.append('lat', String(coord?.lat ?? ''));
            formdata.append('lng', String(coord?.lng ?? ''));
            formdata.append('manualAddress', String(manualAddress ? 1: 0));
            formdata.append('rawAddress', rawAddress ?? '');
            formdata.append('stateId', String(selectedState ?? ''));
            formdata.append('cityId', String(selectedCity ?? ''));
            formdata.append('township', selectedTownship ?? '');
            formdata.append('street', selectedStreet ?? '');
            formdata.append('unit', selectedUnitNumber ?? '');
            formdata.append('postcode', String(selectedPostalCode ?? ''));
            formdata.append('unitNumber', selectedUnitNumber ?? '');
            formdata.append('noTownship', String(noTownship ? 1 : 0));
    
            formdata.append('propertyTypeId', String(selectedPropertyType ?? ''));
            formdata.append('propertySubTypeId', String(selectedPropertySubType ?? ''));
            formdata.append('propertyUnitTypeId', String(selectedPropertyUnitType ?? ''));
            formdata.append('zoningId', String(selectedZoning ?? ''));
            formdata.append('tenureId', String(selectedTenure ?? ''));
            formdata.append('titleTypeId', String(selectedTitleType ?? ''));
            formdata.append('directionId', String(selectedDirection ?? ''));
            formdata.append('yearOfCompletion', yearOfCompletion ?? '');
            formdata.append('landTypeTitleId', String(selectedLandTypeTitle ?? ''));
            formdata.append('isBumiLot', String(isBumiLot));
            
            selectedFacilityBenefits.forEach((id) => {
                formdata.append(`facilityBenefits`, String(id));
            });
    
            formdata.append('noOfBathrooms', String(noOfBathrooms ?? ''));
            formdata.append('noOfBedrooms', String(noOfBedrooms ?? ''));
            formdata.append('noOfMaidRooms', String(noOfMaidRooms ?? ''));
            formdata.append('noOfParkingSpots', String(noOfParkingSpots ?? ''));
            formdata.append('hideBlockNoWhenPublished', String(hideBlockNoWhenPublished ? 1 : 0));
            formdata.append('unitNumberBlock', floorDetails.block ?? '');
            formdata.append('unitNumberFloor', floorDetails.floor ?? '');
            formdata.append('unitNumberUnit', floorDetails.unit ?? '');
            
            formdata.append('builtUp', builtUp ?? '');
            formdata.append('builtUpUnit', selectedBuiltUpUnit ?? '');
            formdata.append('builtUpWidth', builtUpDimensions.width ?? '');
            formdata.append('builtUpLength', builtUpDimensions.length ?? '');
            formdata.append('builtUpDimensionUnit', selectedBuiltUpDimensionUnit ?? '');
            formdata.append('floorLoading', floorLoading ?? '');
            formdata.append('floorLoadingUnit', selectedFloorLoadingUnit ?? '');
            formdata.append('ceilingHeight', ceilingHeight ?? '');
            formdata.append('ceilingHeightUnit', selectedCeilingHeightUnit ?? '');
            formdata.append('landArea', landArea ?? '');
            formdata.append('landAreaUnit', selectedLandAreaUnit ?? '');
            formdata.append('landAreaWidth', landAreaDimensions.width ?? '');
            formdata.append('landAreaLength', landAreaDimensions.length ?? '');
            formdata.append('landAreaDimensionUnit', selectedLandAreaDimensionUnit ?? '');

            formdata.append('furnishingConditionId', String(selectedFurnishingCondition ?? ''));
            formdata.append('electricityPhaseId', String(selectedElectricityPhase ?? ''));
            formdata.append('electricitySupply', String(electricitySupply ?? ''));
            formdata.append('electricitySupplyUnit', String(selectedElectricitySupplyUnit ?? ''));
            formdata.append('totalCargoLifts', String(totalCargoLifts ?? ''));
            formdata.append('totalPassengerLifts', String(totalPassengerLifts ?? ''));
            formdata.append('maximumLiftCapacity', String(maximumLiftCapacity ?? ''));

            selectedUnitFeatures.forEach((id) => {
                formdata.append('unitFeatures', String(id));
            });
    
            formdata.append('selectedPricingType', String(selectedPricingType ?? ''));
            formdata.append('price', String(price ?? ''));
            formdata.append('maintenanceFee', String(maintenanceFee ?? ''));
    
            formdata.append('headline', headline ?? '');
            formdata.append('description', description ?? '');

            formdata.append('owners', JSON.stringify(owners));
            formdata.append('requireOwnerValidation', String(requireOwnerValidation ? 1 : 0));
            formdata.append('isInternal', String(isInternal ? 1 : 0));
            
            const response = await api.post(`/listings/${listingId}/publish`, formdata);
            if(!response.data.status) {
                throw new Error(response.data.error);
            }
            window.location.href = '/listings?page=1&length=10&type=active';
        } catch (err : any) {
            const error = err.response ? err.response.data.error : err.message;
            toast.error(error);
        }
    }

    const stepValidations = [
        listingTypeDetailsRef,
        locationMappingDetailsRef, 
        propertyDetailsRef, 
        unitDetailsRef, 
        priceDetailsRef,  
        descriptionDetailsRef,
        galleryDetailsRef,  
        platformPostingDetailsRef
    ];

    const validateOwners = () => {
        const errors: {[key: number]: string[]} = {};
        let isValid = true;
        
        if(requireOwnerValidation && owners.length === 0) {
            isValid = false;
        }

        owners.forEach((owner, index) => {
            const ownerErrors: string[] = [];
    
            if (!owner.firstName.trim()) ownerErrors.push('firstName');
            if (!owner.lastName.trim()) ownerErrors.push('lastName');
            if (!owner.email.trim()) ownerErrors.push('email');
            else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(owner.email)) ownerErrors.push('email-format');
            
            if (!owner.contact.trim()) ownerErrors.push('contact');
            else if (!/^01\d{8,}$/.test(owner.contact.trim())) ownerErrors.push('contact-format');
    
            if (!owner.dob) ownerErrors.push('dob');
    
            if (ownerErrors.length > 0) {
                errors[index] = ownerErrors;
                isValid = false;
            }
        });
    
        setOwnerValidationErrors(errors);
        return isValid;
    };

    const handleStepClick = (clickedStep: number) => {
        if(forTender && clickedStep === 5) {
            return
        }
        for (let i = 0; i < clickedStep; i++) {
            const ref = stepValidations[i];
            if (!ref?.current) continue;
            const valid = ref.current.callback?.();
            if (!valid) {
                setIndex(i + 1);
                return;
            }
        }
        setIndex(clickedStep);
    };
    
    useEffect(() => {
        getListing();
        getListingCriterias();
    }, [])

    useEffect(() => {
        if(selectedPropertyType) {
            getPropertySubTypes();
        }
    }, [selectedPropertyType]);

    useEffect(() => {
        window.scrollTo({
            top: 0,
            behavior: 'smooth',
        });
    }, [index]);

    useEffect(() => {
        if(selectedListingType !== listing?.listingTypeId && preloaded) {
            // Other
            if(selectedListingType === 3) {
                setSelectedPropertyType(6);
            } else if(selectedListingType === 4) {
                setSelectedPropertyType(7);
            } else if(selectedListingType === 5) {
                setSelectedPropertyType(8);
            } else {
                setSelectedPropertyType(null);
            }
            setSelectedPropertySubType(null);
            setSelectedPropertyUnitType(null);
            setSelectedTenure(null);
            setSelectedTitleType(null);
            setSelectedLandTypeTitle(null);
            setIsBumiLot(null);
        }
    }, [selectedListingType]);

    useEffect(() => {
        if(propertySubTypes && propertySubTypes.length === 1) {
            setSelectedPropertySubType(propertySubTypes[0].id);
        }
    }, [propertySubTypes]);

    useEffect(() => {
        if(selectedListingType !== listing?.listingTypeId) {
            // Other
            if(selectedListingType === 3) {
                setSelectedPropertyType(6);
            } else if(selectedListingType === 4) {
                setSelectedPropertyType(7);
            } else if(selectedListingType === 5) {
                setSelectedPropertyType(8);
            } else {
                setSelectedPropertyType(null);
            }
            setSelectedPropertySubType(null);
            setSelectedPropertyUnitType(null);
            setSelectedTenure(null);
            setSelectedTitleType(null);
            setSelectedLandTypeTitle(null);
            setIsBumiLot(null);
        }
    }, [selectedListingType]);

    useEffect(() => {
        if(propertySubTypes && propertySubTypes.length === 1) {
            setSelectedPropertySubType(propertySubTypes[0].id);
        }
    }, [propertySubTypes]);

    useEffect(() => {
        if(listing && !preloaded) {
            if(listing.submitted === 0 && listing.submitted === 0) {
                setIndex(7);
            }

            setRequireOwnerValidation(listing.requireOwnerValidation ? true : false);
            const owners = listing.owners;
            if(owners.length > 0) {
                setOwners(
                    owners.map((owner: any) => ({
                        firstName: owner.owner?.firstName || '',
                        lastName: owner.owner?.lastName || '',
                        email: owner.owner?.email || '',
                        contact: owner.owner?.contact || '',
                        dob: owner.owner?.dob ? new Date(owner.owner?.dob) : undefined,
                        requestSentAt: owner.requestSentAt,
                        reason: owner.reason,
                        status: owner.status,
                        avatar: owner.owner?.avatar || ''
                    }))
                );
            }

            setJupemNo(listing.jupemNo ?? '');
            setRadius(listing.radius);
            setUseTrueLocation(listing.useTrueLocation == 1);
            setLotNo(listing.lotNo ?? '');
            setSelectedDistrict(listing.districtId ? String(listing.districtId) : '');
            setSelectedSubdistrict(listing.subdistrictId ? String(listing.subdistrictId) : '');
            setSizeOfLand(listing.sizeOfLand ?? '');
            setOwnershipNumber(listing.ownershipNumber ?? '');
            setSelectedTitleTypeCode(listing.titleTypeCode ?? '');
              
            setSelectedListingType(listing.listingTypeId);
            setSelectedProjectType(listing.projectTypeId);
            setSelectedForSaleOrRent(listing.forSaleOrRentId);
            setSelectedLeaseTerm(listing.leaseTermId ?? null);
            setListingReferenceNumber(listing.listingReferenceNumber ?? '');
            setForAuction(listing.forAuction ? true : false);
            setForTender(listing.forTender ? true : false);
            setCoAgencyListing(listing.coAgencyListing ? true : false);
            setCoShareListing(listing.coShareListing ? true : false);
            setSelectedAvailableDate(listing.availableDate ? new Date(listing.availableDate) : undefined);
            setSelectedRentalType(listing.rentalTypeId ?? null);

            setSelectedState(listing.stateId);
            setSelectedCity(listing.cityId);
            setSelectedTownship(listing.township ?? '');
            setSelectedPostalCode(listing.postcode ?? '');
            setSelectedStreet(listing.street ?? '');
            setSelectedUnitNumber(listing.unit ?? '');
            setRawAddress(listing.rawAddress ?? '');
            setSelectedFacilityBenefits(listing.property.facilityBenefits);
            setCoord({
                lat: Number(listing.lat) ?? null,
                lng: Number(listing.lng)
            });

            setSelectedPropertyType(listing.propertyTypeId);
            setSelectedPropertySubType(listing.propertySubTypeId);
            setSelectedPropertyUnitType(listing.propertyUnitTypeId ?? null);
            setIsBumiLot(listing.bumiLot ?? null);
            setSelectedDirection(listing.directionId);
            setSelectedTenure(listing.tenureId);
            setSelectedZoning(listing.zoningId);
            setSelectedTitleType(listing.titleTypeId);
            setSelectedLandTypeTitle(listing.landTitleTypeId ?? null);
            setSelectedFurnishingCondition(listing.furnishingConditionId);
            setSelectedUnitFeatures(listing.property.unitFeatures);
            setYearOfCompletion(String(listing.yearOfCompletion) ?? '');

            // Index 4
            setNoOfBathrooms(listing.noOfBathrooms ?? null);
            setNoOfBedrooms(listing.noOfBedrooms ?? null);
            setNoOfMaidRooms(listing.noOfMaidrooms ?? null);

            setBuiltUp(listing.builtUp);
            setSelectedBuiltUpUnit(listing.builtUpUnit ?? 'sqft');
            setBuiltUpDimensions({
                width: listing.builtUpWidth ?? '',
                length: listing.builtUpLength ?? ''
            });
            setSelectedBuiltUpDimensionUnit(listing.builtUpDimensionUnit ?? 'ft');
            setFloorLoading(listing.floorLoading ?? '');
            setSelectedFloorLoadingUnit(listing.floorLoadingUnit ?? 'tsm');
            setCeilingHeight(listing.ceilingHeight ?? '');
            setSelectedCeilingHeightUnit(listing.ceilingHeightUnit ?? 'ft');
            setLandArea(listing.landArea ?? '');
            setSelectedLandAreaUnit(listing.landAreaUnit ?? 'sqft');
            setLandAreaDimensions({
                width: listing.landAreaWidth ?? '',
                length: listing.landAreaLength ?? ''
            });
            setSelectedLandAreaDimensionUnit(listing.landAreaDimensionUnit ?? 'ft');

            setFloorDetails({
                block: listing.unitNumberBlock ?? '',
                floor: listing.unitNumberFloor ?? '',
                unit: listing.unitNumberUnit ?? ''
            });
            setHideBlockNoWhenPublished(listing.hideBlockNoWhenPublished ? true : false)

            setNoOfParkingSpots(listing.noOfParkingSpots ?? null);

            setSelectedElectricityPhase(listing.electricityPhaseId ?? null);
            setElectricitySupply(listing.electricitySupply ?? null);
            setSelectedElectricitySupplyUnit(listing.electricitySupplyUnit ?? 'amp');

            setTotalCargoLifts(listing.totalCargoLifts ?? null);
            setTotalPassengerLifts(listing.totalPassengerLifts ?? null);
            setMaximumLiftCapacity(listing.maximumLiftCapacity ?? null);

            // Index 5
            setSelectedPricingType(listing.pricingTypeId ?? null);
            setPrice(listing.price);
            setMaintenanceFee(listing.maintenanceFee ?? null);
            
            setHeadline(listing.headline);
            setDescription(listing.description);

            setIsInternal(listing.isInternal === 1);
            setPreloaded(true);
        }
    }, [listing]);

    if(!preloaded) {
        return (
            <div className="space-y-6">
                <Skeleton className="h-8 w-3/4" />
            
                <div className="flex flex-wrap gap-5 mt-5">
                    {
                        [1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
                            <Skeleton key={i} className="h-6 w-32" />
                        ))
                    }
                </div>
    
                <div className="mt-8 space-y-4">
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-2/3" />
                    <Skeleton className="h-32 w-full" />
                    <Skeleton className="h-10 w-full" />
                    <Skeleton className="h-10 w-1/2" />
                </div>
        
                <div className="flex justify-end gap-2 mt-10">
                    <Skeleton className="h-10 w-24" />
                    <Skeleton className="h-10 w-24" />
                </div>
            </div>
        )
    } 

    return listing && preloaded && (
        <div>
            <h3 className='font-bold h3'>{ listing.headline }</h3>
            <div className="flex flex-wrap gap-5 relative border-gray-200 mt-5">
               {
                    steps.map((item, i) => (
                        <div
                        key={item}
                        onClick={() => handleStepClick(i + 1)}
                        className={`relative z-10 transition-colors cursor-pointer ${
                            index === (i + 1) ? 'text-black font-bold' : 'text-gray-600'
                        }`}>
                            <span className={`relative z-10 uppercase text-sm ${index === (i + 1) ? 'text-black' : 'text-gray-400'}`}>{item}</span>
                            {
                                index === (i + 1) && (
                                    <motion.div
                                    layoutId="underline"
                                    className="absolute left-0 right-0 -bottom-1 h-[2px] bg-black"
                                    transition={{ type: 'spring', stiffness: 300, damping: 25 }}
                                    />
                                )
                            }
                        </div>
                    ))
                }
            </div>
            {/* <_Alert variant="default" className='mt-5 bg-blue-100 border-blue-300'>
                <AlertCircle color='blue'/>
                <AlertTitle>Alert!</AlertTitle>
                <AlertDescription>Please proceed to the end to update the listing.</AlertDescription>
            </_Alert> */}
            <form>
                <div className="relative">
                    <div style={{ display: index === 1 ? 'block' : 'none' }}>
                        <ListingTypeDetails
                        ref={listingTypeDetailsRef}
                        listingTypes={listingTypes}
                        leaseTerms={leaseTerms}
                        rentalTypes={rentalTypes}
                        projectTypes={projectTypes}
                        selectedProjectType={selectedProjectType}
                        setSelectedProjectType={setSelectedProjectType}
                        forSaleOrRentOptions={forSaleOrRentOptions}
                        selectedListingType={selectedListingType}
                        setSelectedListingType={setSelectedListingType}
                        selectedLeaseTerm={selectedLeaseTerm}
                        setSelectedLeaseTerm={setSelectedLeaseTerm}
                        selectedForSaleOrRent={selectedForSaleOrRent}
                        setSelectedForSaleOrRent={setSelectedForSaleOrRent}
                        selectedRentalType={selectedRentalType}
                        setSelectedRentalType={setSelectedRentalType}
                        selectedAvailableDate={selectedAvailableDate}
                        setSelectedAvailableDate={setSelectedAvailableDate}
                        listingReferenceNumber={listingReferenceNumber}
                        setListingReferenceNumber={setListingReferenceNumber}
                        coAgencyListing={coAgencyListing}
                        setCoAgencyListing={setCoAgencyListing}
                        coShareListing={coShareListing}
                        setCoShareListing={setCoShareListing}
                        forAuction={forAuction}
                        setForAuction={setForAuction}
                        forTender={forTender}
                        setForTender={setForTender}
                        selectedCoShareAgents={selectedCoShareAgents}
                        setSelectedCoShareAgents={setSelectedCoShareAgents}
                        selectedCoAgencyAgents={selectedCoAgencyAgents}
                        setSelectedCoAgencyAgents={setSelectedCoAgencyAgents}
                        listingId={listing.id ?? null}
                        edit={false}/>
                    </div>
                    <div style={{ display: index === 2 ? 'block' : 'none' }}>
                        
                        <PropertyDetails
                        ref={propertyDetailsRef}
                        propertyTypes={propertyTypes.filter(x => x.categoryId === selectedListingType)}
                        propertySubTypes={propertySubTypes}
                        propertyUnitTypes={propertyUnitTypes}
                        tenures={tenures}
                        zoning={zoning}
                        titleTypes={titleTypes}
                        directions={directions}
                        selectedDirection={selectedDirection}
                        setSelectedDirection={setSelectedDirection}
                        selectedTenure={selectedTenure}
                        setSelectedTenure={setSelectedTenure}
                        selectedTitleType={selectedTitleType}
                        setSelectedTitleType={setSelectedTitleType}
                        selectedProjectType={selectedProjectType}
                        selectedPropertyType={selectedPropertyType}
                        setSelectedPropertyType={setSelectedPropertyType}
                        selectedPropertySubType={selectedPropertySubType}
                        setSelectedPropertySubType={setSelectedPropertySubType}
                        selectedPropertyUnitType={selectedPropertyUnitType}
                        setSelectedPropertyUnitType={setSelectedPropertyUnitType}
                        selectedZoning={selectedZoning}
                        setSelectedZoning={setSelectedZoning}
                        yearOfCompletion={yearOfCompletion}
                        setYearOfCompletion={setYearOfCompletion}
                        facilityBenefits={facilityBenefits}
                        selectedFacilityBenefits={selectedFacilityBenefits}
                        setSelectedFacilityBenefits={setSelectedFacilityBenefits}
                        selectedLandTypeTitle={selectedLandTypeTitle}
                        setSelectedLandTypeTitle={setSelectedLandTypeTitle}
                        isBumiLot={isBumiLot}
                        setIsBumiLot={setIsBumiLot}
                        landTypeTitles={landTypeTitles}
                        edit={false}/>
                    </div>
                    <div style={{ display: index === 3 ? 'block' : 'none' }}>
                        <LocationMappingDetails
                        ref={locationMappingDetailsRef}
                        manualAddress={manualAddress}
                        setManualAddress={setManualAddress}
                        states={states}
                        rawAddress={rawAddress}
                        setRawAddress={setRawAddress}
                        selectedState={selectedState}
                        setSelectedState={setSelectedState}
                        selectedCity={selectedCity}
                        setSelectedCity={setSelectedCity}
                        selectedTownship={selectedTownship}
                        setSelectedTownship={setSelectedTownship}
                        selectedStreet={selectedStreet}
                        setSelectedStreet={setSelectedStreet}
                        selectedPostalCode={selectedPostalCode}
                        setSelectedPostalCode={setSelectedPostalCode}
                        selectedUnitNumber={selectedUnitNumber}
                        setSelectedUnitNumber={setSelectedUnitNumber}
                        noTownship={noTownship}
                        setNoTownship={setNoTownship}
                        coord={coord}
                        setCoord={setCoord} 
                        districts={districts}
                        subdistricts={subdistricts}
                        landTitleTypes={landTitleTypes}
                        selectedDistrict={selectedDistrict}
                        setSelectedDistrict={setSelectedDistrict}
                        selectedSubdistrict={selectedSubdistrict}
                        setSelectedSubdistrict={setSelectedSubdistrict}
                        selectedTitleTypeCode={selectedTitleTypeCode}
                        setSelectedTitleTypeCode={setSelectedTitleTypeCode}
                        jupemNo={jupemNo}
                        setJupemNo={setJupemNo}
                        ownershipNumber={ownershipNumber}
                        setOwnershipNumber={setOwnershipNumber}
                        sizeOfLand={sizeOfLand}
                        setSizeOfLand={setSizeOfLand}
                        lotNo={lotNo}
                        setLotNo={setLotNo}
                        setReferenceListing={setReferenceListing}
                        markAsDuplicate={markAsDuplicate}
                        setMarkAsDuplicate={setMarkAsDuplicate}
                        edit={true}
                        listing={listing}
                        excludeMapPin={excludeMapPin}
                        setExcludeMapPin={setExcludeMapPin}
                        selectedPropertyType={selectedPropertyType}
                        selectedPropertySubType={selectedPropertySubType}
                        radius={radius}
                        setRadius={setRadius}
                        electricityBill={electricityBill}
                        setElectricityBill={setElectricityBill}
                        waterBill={waterBill}
                        setWaterBill={setWaterBill}
                        useTrueLocation={useTrueLocation}
                        setUseTrueLocation={setUseTrueLocation}
                        selectedListingType={selectedListingType}/>
                    </div>
                    <div style={{ display: index === 4 ? 'block' : 'none' }}>
                        <UnitDetails
                        ref={unitDetailsRef}
                        selectedListingType={selectedListingType}
                        electricityPhases={electricityPhases}
                        unitFeatures={unitFeatures}
                        furnishingConditions={furnishingConditions}
                        noOfBathrooms={noOfBathrooms}
                        setNoOfBathrooms={setNoOfBathrooms}
                        noOfBedrooms={noOfBedrooms}
                        setNoOfBedrooms={setNoOfBedrooms}
                        noOfMaidRooms={noOfMaidRooms}
                        setNoOfMaidRooms={setNoOfMaidRooms}
                        noOfParkingSpots={noOfParkingSpots}
                        setNoOfParkingSpots={setNoOfParkingSpots}
                        builtUp={builtUp}
                        setBuiltUp={setBuiltUp}
                        selectedBuiltUpUnit={selectedBuiltUpUnit}
                        setSelectedBuiltUpUnit={setSelectedBuiltUpUnit}
                        floorDetails={floorDetails}
                        setFloorDetails={setFloorDetails}
                        floorLoading={floorLoading}
                        setFloorLoading={setFloorLoading}
                        selectedFloorLoadingUnit={selectedFloorLoadingUnit}
                        setSelectedFloorLoadingUnit={setSelectedFloorLoadingUnit}
                        ceilingHeight={ceilingHeight}
                        setCeilingHeight={setCeilingHeight}
                        selectedCeilingHeightUnit={selectedCeilingHeightUnit}
                        setSelectedCeilingHeightUnit={setSelectedCeilingHeightUnit}
                        hideBlockNoWhenPublished={hideBlockNoWhenPublished}
                        setHideBlockNoWhenPublished={setHideBlockNoWhenPublished}
                        builtUpDimensions={builtUpDimensions}
                        setBuiltUpDimensions={setBuiltUpDimensions}
                        selectedBuiltUpDimensionUnit={selectedBuiltUpDimensionUnit}
                        setSelectedBuiltUpDimensionUnit={setSelectedBuiltUpDimensionUnit}
                        landArea={landArea}
                        setLandArea={setLandArea}
                        selectedLandAreaUnit={selectedLandAreaUnit}
                        setSelectedLandAreaUnit={setSelectedLandAreaUnit}
                        landAreaDimensions={landAreaDimensions}
                        setLandAreaDimensions={setLandAreaDimensions}
                        selectedLandAreaDimensionUnit={selectedLandAreaDimensionUnit}
                        setSelectedLandAreaDimensionUnit={setSelectedLandAreaDimensionUnit}
                        selectedUnitFeatures={selectedUnitFeatures}
                        setSelectedUnitFeatures={setSelectedUnitFeatures}
                        selectedFurnishingCondition={selectedFurnishingCondition}
                        setSelectedFurnishingCondition={setSelectedFurnishingCondition}
                        selectedElectricityPhase={selectedElectricityPhase}
                        setSelectedElectricityPhase={setSelectedElectricityPhase}
                        electricitySupply={electricitySupply}
                        setElectricitySupply={setElectricitySupply}
                        selectedElectricitySupplyUnit={selectedElectricitySupplyUnit}
                        setSelectedElectricitySupplyUnit={setSelectedElectricitySupplyUnit}
                        totalCargoLifts={totalCargoLifts}
                        setTotalCargoLifts={setTotalCargoLifts}
                        totalPassengerLifts={totalPassengerLifts}
                        setTotalPassengerLifts={setTotalPassengerLifts}
                        maximumLiftCapacity={maximumLiftCapacity}
                        setMaximumLiftCapacity={setMaximumLiftCapacity}
                        selectedPropertyType={selectedPropertyType}
                        selectedPropertySubType={selectedPropertySubType}

                        builtUpUnits={builtUpUnits}
                        landAreaUnits={landAreaUnits}
                        builtUpDimensionUnits={builtUpDimensionUnits}
                        landAreaDimensionUnits={landAreaDimensionUnits}
                        floorLoadingUnits={floorLoadingUnits}
                        electricitySupplyUnits={electricitySupplyUnits}
                        ceilingHeightUnits={ceilingHeightUnits}
                        edit={false}/>
                    </div>
                    <div style={{ display: index === 5 ? 'block' : 'none' }}>
                        <PriceDetails
                        ref={priceDetailsRef}
                        selectedForSaleOrRent={selectedForSaleOrRent}
                        pricingTypes={pricingTypes}
                        selectedPricingType={selectedPricingType}
                        setSelectedPricingType={setSelectedPricingType}
                        price={price}
                        setPrice={setPrice}
                        maintenanceFee={maintenanceFee}
                        setMaintenanceFee={setMaintenanceFee}
                        builtUp={builtUp}
                        landArea={landArea}
                        forTender={forTender}
                        edit={false}/>
                    </div>
                    <div style={{ display: index === 6 ? 'block' : 'none' }}>
                        <DescriptionDetails
                        ref={descriptionDetailsRef}
                        description={description}
                        setDescription={setDescription}
                        headline={headline}
                        setHeadline={setHeadline}
                        edit={false}
                        aiSlot={
                            <div className='flex flex-wrap gap-2'>
                                <AIListingAssistant
                                    mode='generate'
                                    listingId={Number(listingId)}
                                    getDraft={() => ({ headline, description })}
                                    onAccept={applyAICopy} />
                                <AIListingAssistant
                                    mode='enhance'
                                    listingId={Number(listingId)}
                                    currentDescription={description}
                                    onAccept={applyAICopy} />
                            </div>
                        }/>
                    </div>
                    <div style={{ display: index === 7 ? 'block' : 'none' }}>
                        <GalleryDetails
                        ref={galleryDetailsRef}
                        listing={listing}
                        setListing={setListing}
                        edit={false}/>
                    </div>
                    <div style={{ display: index === 8 ? 'block' : 'none' }}>
                        <PlatformPostingDetails
                        ref={platformPostingDetailsRef}
                        listing={listing}
                        requireOwnerValidation={requireOwnerValidation}
                        setRequireOwnerValidation={setRequireOwnerValidation}
                        ownerValidationErrors={ownerValidationErrors}
                        setOwnerValidationErrors={setOwnerValidationErrors}
                        owners={owners}
                        setOwners={setOwners}
                        edit={false}
                        isInternal={isInternal}
                        setIsInternal={setIsInternal}/>
                    </div>
                </div>
                <div className='my-10 flex justify-end'>
                    <Button 
                    className='mr-1' 
                    variant={'secondary'}
                    onClick={(e) => {
                        e.preventDefault();
                        if(index === 1) {
                            navigate('/listings?page=1&length=10&type=active')
                        } else if(index > 1) {
                            setIndex((prev) => (prev > 1 ? prev - 1 : 1));
                        }
                    }}>BACK</Button>
                    {
                        index !== 8 ? (
                            <Button 
                                onClick={handleSubmission} 
 
                                disabled={submitting}>
                                NEXT
                            </Button>
                        ) : (
                            <>
                                {
                                    requireOwnerValidation && listing.submitted === 0 && listing.published === 0 ? (
                                        <Alert 
                                        trigger={
                                            <Button
 
                                                disabled={submitting}>
                                                {submitting ? 'SUBMITTING' : 'SUBMIT'}
                                                {submitting && <Spinner />}
                                            </Button>
                                        }
                                        description={
                                            listing.submitted === 0 
                                                ? 'This will send an approval email request to the owner and upon approval, the listing will be automatically published.'
                                                : 'Please ensure all listing details are correct before updating. This action will update the listing with all current information.'
                                        }
                                        onConfirm={() => {
                                            if (requireOwnerValidation && owners.length > 0 && listing.published === 0) {
                                                const isValid = validateOwners();
                                                if (!isValid) {
                                                    toast.error('Please fix owner validation errors before updating.');
                                                    return;
                                                }
                                            }
                                            handleSubmission({ preventDefault: () => {} });
                                        }} />
                                    ) : !requireOwnerValidation && listing.published === 0 && listing.submitted === 0 ? (
                                        <Alert 
                                        trigger={
                                            <Button
 
                                                disabled={submitting}>
                                                {submitting ? 'PUBLISHING' : 'PUBLISH'}
                                                {submitting && <Spinner />}
                                            </Button>
                                        }
                                        description='Once published, location and listing type cannot be edited in the draft versions.'
                                        onConfirm={() => {
                                            handleSubmission({ preventDefault: () => {} })
                                        }}/>
                                    ) : (
                                        <Alert 
                                        trigger={
                                            <Button
 
                                                disabled={submitting}>
                                                {submitting ? 'UPDATING' : 'UPDATE'}
                                                {submitting && <Spinner />}
                                            </Button>
                                        }
                                        description='Please ensure all listing details are correct before updating. This action will update the listing with all current information.'
                                        onConfirm={() => {
                                            // if (requireOwnerValidation && owners.length > 0) {
                                            //     const isValid = validateOwners();
                                            //     if (!isValid) {
                                            //         toast.error('Please fix owner validation errors before updating.');
                                            //         return;
                                            //     }
                                            // }
                                            handleSubmission({ preventDefault: () => {} });
                                        }}/>
                                    )
                                }
                            </>
                        )
                    }
                </div>
            </form>
        </div>
    )
}

export default ListingEdit